import { ChevronRight, ArrowUpRight } from "lucide-react";
import { ORDO_NODES, RESPONSIBILITY_IDS } from "@/content/ordo";
import { Mono } from "@/components/site/primitives";
import { cn } from "@/lib/utils";

const NodeBtn = ({ id, sel, related, onSelect, className, children }) => {
  const on = sel === id;
  const dim = related && !related.has(id);
  return (
    <button type="button" onClick={() => onSelect(id)} aria-pressed={on} data-testid={`arch-node-${id}`}
      className={cn("relative border text-left transition-[opacity,background-color,border-color] duration-200",
        on ? "border-o-accent bg-o-soft" : "border-o-line hover:border-o-strong", dim && "opacity-40", className)}>
      {children}
    </button>
  );
};

const Responsibilities = (p) => (
  <div className="grid gap-2 md:grid-cols-5">
    {RESPONSIBILITY_IDS.map((id, i) => (
      <div key={id} className="relative">
        <NodeBtn id={id} {...p} className="flex h-full w-full items-center gap-4 p-3 md:min-h-[104px] md:flex-col md:items-start md:justify-between">
          <Mono accent={p.sel === id}>{ORDO_NODES[id].index}</Mono>
          <span className="font-display text-[15px] font-bold leading-tight">{ORDO_NODES[id].label}</span>
        </NodeBtn>
        {i < 4 && <ChevronRight aria-hidden className="absolute -right-[12px] top-1/2 z-10 hidden h-4 w-4 -translate-y-1/2 text-o-dim md:block" />}
      </div>
    ))}
  </div>
);

const Band = ({ id, kind, ...p }) => (
  <NodeBtn id={id} {...p} className={cn("w-full px-4 py-3", p.className)}>
    <Mono>{kind}</Mono>
    <span className="mt-1 block font-display text-[15px] font-bold leading-tight">{ORDO_NODES[id].label}</span>
    <span className="mt-0.5 block text-xs text-o-dim">{ORDO_NODES[id].tagline}</span>
  </NodeBtn>
);

export const ArchitectureDiagram = ({ sel, related, onSelect }) => {
  const p = { sel, related, onSelect };
  return (
    <div className="space-y-3" data-testid="architecture-diagram">
      <NodeBtn id="infrastructure" {...p} className="flex w-full items-center justify-between gap-4 border-dashed px-4 py-3">
        <span>
          <Mono>Separate licensing route</Mono>
          <span className="mt-1 block font-display text-[15px] font-bold">General Ordo Infrastructure</span>
        </span>
        <span className="hidden max-w-[260px] text-right text-xs text-o-dim sm:block">The architecture as a whole, carried into other organizations' intelligent systems</span>
        <ArrowUpRight className="h-4 w-4 shrink-0 text-o-dim" />
      </NodeBtn>
      <div aria-hidden className="flex justify-center"><span className="h-5 w-px bg-o-strong" /></div>
      <div className={cn("border p-3 transition-colors sm:p-4", sel === "whole" ? "border-o-accent" : "border-o-strong")}>
        <button type="button" onClick={() => onSelect("whole")} aria-pressed={sel === "whole"} data-testid="arch-node-whole"
          className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-o-accent hover:underline">
          General Ordo · governing architecture
        </button>
        <Responsibilities {...p} />
        <div aria-hidden className="mx-[10%] mt-2 hidden h-4 border-x border-b border-dashed border-o-strong md:block" />
        <p className="mb-3 mt-2 md:text-center"><Mono>Continuing standing returns to evidence</Mono></p>
        <div className="grid gap-2 md:grid-cols-5">
          <Band id="engine" kind="Ordo software family" {...p} className="md:col-span-4" />
          <Band id="stewardship" kind="Family" {...p} />
        </div>
      </div>
      <div className="pt-3">
        <div className="mb-3 flex items-center gap-3">
          <span aria-hidden className="h-px flex-1 border-t border-dashed border-o-strong" />
          <Mono>Related applied capabilities · separate offerings, not subdivisions</Mono>
          <span aria-hidden className="h-px flex-1 border-t border-dashed border-o-strong" />
        </div>
        <div className="grid gap-2 md:grid-cols-5">
          <Band id="breakpoint" kind="Independent" {...p} className="border-dashed md:col-span-2" />
          <Band id="mesh" kind="Independent" {...p} className="border-dashed md:col-span-2 md:col-start-3" />
        </div>
      </div>
      <p className="pt-2"><span className="draft-tag">Provisional relationships · confirmed in Phase 3</span></p>
    </div>
  );
};
